// @ts-nocheck
/*
 * Port of tts/voice-profile.js (451 lines). BYTE-IDENTICAL.
 * Voice-profile create/edit form. When navigated to with
 * `location.state.profile`, populates existing values and switches the
 * submit label to "Update Profile"; otherwise creates a new one.
 * `category` is hardcoded to "General" (v1 dropped the picker).
 * Tone/pitch/accent are stored as label strings in the API but rendered
 * from the value → label maps (option lists match those in add-tts).
 * All layout comes from add-tts.scss — no bespoke styles here.
 */
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { postApiCall } from '@/service/postApiCall';
import { API_VOICE_PROFILE } from '@/service/url';
import { ROUTES, USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import { AclDenied } from '@/components/feedback/AclDenied';
import { toast } from 'sonner';
import {
  FaMicrophone,
  FaGlobe,
  FaVenusMars,
  FaMale,
  FaFemale,
  FaSlidersH,
  FaUserAlt,
  FaVolumeUp,
  FaStar,
  FaCheckCircle,
  FaSave,
} from 'react-icons/fa';
import './add-tts.scss';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as ShadButton } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Loader from '@/components/partials/components/Loader';

/* ─── Same option lists as add-tts ──────────────────────────────────────── */
const LANGUAGE_TONE_OPTIONS = [
  { value: 'teenager', label: 'Teenager', desc: 'Youthful, energetic tone' },
  { value: 'young_adult', label: 'Young Adult', desc: 'Confident, modern tone' },
  { value: 'middle_aged', label: 'Middle-Aged', desc: 'Mature, professional tone' },
  { value: 'elderly', label: 'Elderly', desc: 'Calm, measured tone' },
];

const PITCH_OPTIONS = [
  { value: 'very_low', label: 'Very Low Pitch' },
  { value: 'low', label: 'Low Pitch' },
  { value: 'moderate', label: 'Moderate Pitch' },
  { value: 'high', label: 'High Pitch' },
  { value: 'very_high', label: 'Very High Pitch' },
];

const ACCENT_OPTIONS = [
  { value: 'american', label: 'American Accent' },
  { value: 'british', label: 'British Accent' },
  { value: 'australian', label: 'Australian Accent' },
  { value: 'canadian', label: 'Canadian Accent' },
  { value: 'indian', label: 'Indian Accent' },
  { value: 'chinese', label: 'Chinese Accent' },
];

/* ─── label helpers (match add-tts instruct format) ─────────────────────── */
const toneLabel = (v: string) =>
  LANGUAGE_TONE_OPTIONS.find((t) => t.value === v)?.label?.toLowerCase() || v;
const pitchLabel = (v: string) =>
  PITCH_OPTIONS.find((p) => p.value === v)?.label?.toLowerCase() || v;
const accentLabel = (v: string) =>
  ACCENT_OPTIONS.find((a) => a.value === v)?.label?.toLowerCase() || v;

const toneFromLabel = (l: any) =>
  LANGUAGE_TONE_OPTIONS.find((t) => t.label.toLowerCase() === l?.toLowerCase())
    ?.value || l;
const pitchFromLabel = (l: any) =>
  PITCH_OPTIONS.find((p) => p.label.toLowerCase() === l?.toLowerCase())?.value || l;
const accentFromLabel = (l: any) =>
  ACCENT_OPTIONS.find((a) => a.label.toLowerCase() === l?.toLowerCase())?.value || l;

/* ─── Component ──────────────────────────────────────────────────────────── */
const VoiceProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const editProfile = (location.state as any)?.profile || null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileId, setProfileId] = useState<any>(editProfile?.id || null);

  const [flowName, setFlowName] = useState('');
  const [gender, setGender] = useState('FEMALE');
  const category = 'General'; // Category is fixed to General; no longer user-selectable.
  const [languageTone, setLanguageTone] = useState('middle_aged');
  const [pitch, setPitch] = useState('low');
  const [accent, setAccent] = useState('indian');
  const [speed, setSpeed] = useState(1.0);
  const ttsAcl = useModuleAcl(MODULE_ACL.TTS);

  const populateFromProfile = (p: any) => {
    if (!p) return;
    setProfileId(p.id);
    setFlowName(p.flow_name || '');
    setGender((p.gender || 'female').toUpperCase());
    setLanguageTone(toneFromLabel(p.language_tone) || 'middle_aged');
    setPitch(pitchFromLabel(p.pitch) || 'low');
    setAccent(accentFromLabel(p.accent) || 'indian');
    setSpeed(p.speed != null ? Number(p.speed) : 1.0);
  };

  useEffect(() => {
    if (editProfile) populateFromProfile(editProfile);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedToneLabel =
    LANGUAGE_TONE_OPTIONS.find((t) => t.value === languageTone)?.label || '';
  const selectedPitchLabel =
    PITCH_OPTIONS.find((p) => p.value === pitch)?.label || '';
  const selectedAccentLabel =
    ACCENT_OPTIONS.find((a) => a.value === accent)?.label || '';

  const handleSave = async () => {
    if (!flowName.trim() || !gender || !languageTone || !pitch || !accent) {
      toast.warning('Please fill all fields before saving.');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        ...(profileId && { id: profileId }),
        flow_name: flowName.trim(),
        gender: gender.toLowerCase(),
        category,
        language_tone: toneLabel(languageTone),
        pitch: pitchLabel(pitch),
        accent: accentLabel(accent),
        speed,
      };
      const res: any = await postApiCall(API_VOICE_PROFILE, payload);
      if (res?.id) setProfileId(res.id);
      toast.success(
        profileId
          ? 'Voice Profile updated successfully!'
          : 'Voice Profile saved successfully!',
      );
      setTimeout(() => navigate('/' + ROUTES.TTS.VOICE_PROFILE_LIST), 1200);
    } catch {
      toast.error('Failed to save voice profile.');
    } finally {
      setSaving(false);
    }
  };

  const role = getUser()?.role || null;
  const isSuperAdmin = role === USER_ROLES.SUPERADMIN;
  const isCallCentreAdmin = role === USER_ROLES.CALL_CENTRE_ADMIN;
  const writeOperation = editProfile ? 'edit' : 'add';
  const allowed = editProfile?.is_default
    ? isSuperAdmin
    : isSuperAdmin ||
      (isCallCentreAdmin && (editProfile ? ttsAcl.canEdit : ttsAcl.canAdd));
  if (!allowed) return <AclDenied operation={writeOperation} />;

  if (loading) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title={profileId ? 'Edit Voice Profile' : 'New Voice Profile'}
        description={
          profileId
            ? 'Update your OMNI Voice profile settings.'
            : 'Configure a new OMNI Voice profile.'
        }
        actions={
          <ShadButton asChild variant="outline" size="sm">
            <Link to={'/' + ROUTES.TTS.VOICE_PROFILE_LIST}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </ShadButton>
        }
      />
      <div className="tts-page">
      {/* ── Stats strip ─────────────────────────────────────────── */}
      <div className="tts-stats-strip">
        <div className="tts-stat-card">
          <div className="tts-stat-card__icon" style={{ color: 'hsl(var(--primary))' }}>
            <FaStar />
          </div>
          <div>
            <div className="tts-stat-card__label">Flow Name</div>
            <div className="tts-stat-card__value">{flowName || '—'}</div>
          </div>
        </div>

        <div className="tts-stat-card">
          <div className="tts-stat-card__icon" style={{ color: 'hsl(var(--primary))' }}>
            <FaVenusMars />
          </div>
          <div>
            <div className="tts-stat-card__label">Gender</div>
            <div className="tts-stat-card__value">
              {gender ? gender.charAt(0) + gender.slice(1).toLowerCase() : '—'}
            </div>
          </div>
        </div>

        <div className="tts-stat-card">
          <div className="tts-stat-card__icon" style={{ color: 'hsl(var(--primary))' }}>
            <FaStar />
          </div>
          <div>
            <div className="tts-stat-card__label">TTS Operator</div>
            <div className="tts-stat-card__value">EXPRESS INTERNAL</div>
          </div>
        </div>
      </div>

      {/* ── Main content ────────────────────────────────────────── */}
      <div className="tts-content">
        {/* ── Form card ───────────────────────────────────────────── */}
        <div className="tts-form-card">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-3 px-6 pt-5">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                <FaSlidersH className="h-4 w-4" />
              </span>
              <div>
                <div className="text-sm font-semibold text-foreground">
                  Voice Configuration
                </div>
                <div className="text-xs text-muted-foreground">
                  Tone, pitch, accent and speed for this profile.
                </div>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
              <FaStar /> EXPRESS INTERNAL
            </span>
          </div>

          <div className="tts-form-card__body">
            {/* ── Voice Settings ────────────────────────────────── */}
            <div className="tts-section">
              <div className="tts-section-title">
                <FaVolumeUp /> Voice Settings
              </div>

              {/* Flow Name — unique per user */}
              <div className="tts-field-group" style={{ marginBottom: '1.1rem' }}>
                <label className="tts-field-label">
                  <FaStar /> Flow Name{' '}
                  <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  className="tts-input"
                  placeholder="e.g. customer_support_female, ivr_hindi_male"
                  value={flowName}
                  onChange={(e) => setFlowName(e.target.value)}
                />
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: 'hsl(var(--muted-foreground))',
                    marginTop: '0.3rem',
                  }}
                >
                  Unique name to identify this profile. Used in the TTS dropdown.
                </div>
              </div>

              {/* Row 2: Language Tone | Speed */}
              <div className="tts-field-row" style={{ marginTop: '1.1rem' }}>
                <div className="tts-field-group">
                  <label className="tts-field-label">
                    <FaUserAlt /> Language Tone
                  </label>
                  <select
                    className="tts-select"
                    value={languageTone}
                    onChange={(e) => setLanguageTone(e.target.value)}
                  >
                    <option value="">Choose tone…</option>
                    {LANGUAGE_TONE_OPTIONS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="tts-field-group">
                  <label className="tts-field-label">
                    <FaVolumeUp /> Speed
                  </label>
                  <div className="tts-slider-track">
                    <input
                      type="range"
                      className="tts-slider"
                      min={0.55}
                      max={2.0}
                      step={0.05}
                      value={speed}
                      onChange={(e) => setSpeed(Number(e.target.value))}
                    />
                    <span className="tts-slider-value">{speed.toFixed(2)}</span>
                  </div>
                  <div className="tts-slider-minmax">
                    <span>0.55</span>
                    <span>2.00</span>
                  </div>
                </div>
              </div>

              {/* Tone pills / selected banner */}
              {!languageTone && (
                <div className="tts-tone-pills">
                  {LANGUAGE_TONE_OPTIONS.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      className="tts-tone-pill"
                      onClick={() => setLanguageTone(t.value)}
                    >
                      <span className="tts-tone-pill__label">{t.label}</span>
                      <span className="tts-tone-pill__desc">{t.desc}</span>
                    </button>
                  ))}
                </div>
              )}

              {languageTone && (
                <div className="tts-tone-selected">
                  <FaCheckCircle className="text-success" />
                  <span>
                    Tone: <strong>{selectedToneLabel}</strong> —{' '}
                    {
                      LANGUAGE_TONE_OPTIONS.find((t) => t.value === languageTone)
                        ?.desc
                    }
                  </span>
                  <button
                    type="button"
                    className="tts-tone-change"
                    onClick={() => setLanguageTone('')}
                  >
                    Change
                  </button>
                </div>
              )}

              {/* Row 3: Gender tiles */}
              <div className="tts-field-group" style={{ marginTop: '1.1rem' }}>
                <label className="tts-field-label">
                  <FaVenusMars /> Gender
                </label>
                <div className="tts-gender-row">
                  <div
                    className={`tts-gender-tile tts-gender-tile--male ${
                      gender === 'MALE' ? 'active' : ''
                    }`}
                    onClick={() => setGender('MALE')}
                  >
                    <div className="tts-gender-tile__icon">
                      <FaMale />
                    </div>
                    <span className="tts-gender-tile__name">Male</span>
                    <span className="tts-gender-tile__desc">
                      Deep &amp; clear voice
                    </span>
                  </div>
                  <div
                    className={`tts-gender-tile tts-gender-tile--female ${
                      gender === 'FEMALE' ? 'active' : ''
                    }`}
                    onClick={() => setGender('FEMALE')}
                  >
                    <div className="tts-gender-tile__icon">
                      <FaFemale />
                    </div>
                    <span className="tts-gender-tile__name">Female</span>
                    <span className="tts-gender-tile__desc">
                      Warm &amp; natural voice
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Voice Modulation ──────────────────────────────── */}
            <div className="tts-section">
              <div className="tts-section-title">
                <FaSlidersH /> Voice Modulation
              </div>

              {/* Row 1: Pitch | Accent */}
              <div className="tts-field-row">
                <div className="tts-field-group">
                  <label className="tts-field-label">
                    <FaSlidersH /> Pitch
                  </label>
                  <select
                    className="tts-select"
                    value={pitch}
                    onChange={(e) => setPitch(e.target.value)}
                  >
                    <option value="">Choose pitch…</option>
                    {PITCH_OPTIONS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="tts-field-group">
                  <label className="tts-field-label">
                    <FaGlobe /> Accent
                  </label>
                  <select
                    className="tts-select"
                    value={accent}
                    onChange={(e) => setAccent(e.target.value)}
                  >
                    <option value="">Choose accent…</option>
                    {ACCENT_OPTIONS.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end border-t border-border/60 pt-4">
              <ShadButton
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="gap-2"
              >
                {saving ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <FaSave />
                )}
                {profileId ? 'Update Profile' : 'Save Profile'}
              </ShadButton>
            </div>
          </div>
        </div>

        {/* ── Right sidebar ─────────────────────────────────────── */}
        <div className="tts-sidebar">
          <div className="tts-sidebar-card">
            <div className="tts-sidebar-card__header">
              <div className="tts-sidebar-card__header-icon text-primary">
                <FaCheckCircle />
              </div>
              <h6>Profile Summary</h6>
            </div>
            <div className="tts-sidebar-card__body">
              <div className="tts-summary-list">
                {[
                  { icon: <FaStar />, label: 'Flow Name', val: flowName },
                  {
                    icon: <FaMicrophone />,
                    label: 'Operator',
                    val: 'EXPRESS INTERNAL',
                  },
                  {
                    icon: <FaVenusMars />,
                    label: 'Gender',
                    val: gender
                      ? gender.charAt(0) + gender.slice(1).toLowerCase()
                      : '',
                  },
                  { icon: <FaUserAlt />, label: 'Tone', val: selectedToneLabel },
                  { icon: <FaSlidersH />, label: 'Pitch', val: selectedPitchLabel },
                  { icon: <FaGlobe />, label: 'Accent', val: selectedAccentLabel },
                  {
                    icon: <FaVolumeUp />,
                    label: 'Speed',
                    val: `${speed.toFixed(2)}x`,
                  },
                ].map((r: any, i: number) => (
                  <React.Fragment key={i}>
                    {i > 0 && <div className="tts-summary-divider" />}
                    <div className="tts-summary-row">
                      <span className="tts-summary-row__label">
                        {r.icon} {r.label}
                      </span>
                      <span
                        className={`tts-summary-row__val${
                          r.val ? '' : ' tts-summary-row__val--empty'
                        }`}
                      >
                        {r.val || 'Not set'}
                      </span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </FillPage>
  );
};

export default VoiceProfile;
